import { Badge, Spin, Flex } from 'antd';
import { CampaignStatuses, Campaign } from '@copilot/common/hooks/campaign/campaignTypes';
import { partition } from 'lodash';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import { BASE_APP_PATHS } from '../../routerConstants';
import { getIcon } from '@copilot/common/components/menus/sidebar';
import { getCampaignTypeIcon } from '@copilot/common/utils/campaign';
import { createCaseInsensitiveSorter } from '@copilot/common/utils/common/sort';
import { LoadingOutlined } from '@ant-design/icons';
import { InboxDisplayType } from '@copilot/data/requests/models';
import { SidebarNotificationMap } from './sideBarTypes';
import { MenuItemType } from '@copilot/common/types/antd';

/**
 * Convert campaigns to menu items
 * @param campaigns - The campaigns to convert
 * @returns The menu items
 */
export function toCampaignMenuItems(
    campaigns: ReadonlyArray<Campaign>
): ReadonlyArray<MenuItemType> {
    const cleaned = campaigns
        .filter((campaign) => !isNil(campaign.name) && !isNil(campaign.type))
        .map((campaign) => ({
            status: campaign.status,
            label: isString(campaign.name) ? campaign.name : '',
            icon: !isNil(campaign.type) ? getIcon(getCampaignTypeIcon(campaign.type)) : undefined,
            key: `/campaign/${campaign.id}`,
        }))
        .sort(createCaseInsensitiveSorter<{ label: string }>((item) => item.label));

    const [enabled, rest] = partition(
        cleaned,
        (campaign) => campaign.status === CampaignStatuses.Enabled
    );
    return [...enabled, ...rest].slice(0, 5);
}

/**
 * Get the open and active keys for the sidebar to highlight and expand the proper menu items
 * @param path The path of the current page
 * @returns The open and active keys
 */
export function getOpenAndActiveKeys(path: string): { openKeys: string[]; activeKeys: string[] } {
    let openKeys: string[] = [];
    let activeKeys: string[] = [path];
    const [_, root, ..._pathSegments] = path.split('/');
    const pathRoot = `/${root}`;
    switch (pathRoot) {
        case BASE_APP_PATHS.Settings:
            activeKeys = [pathRoot];
            break;
        case BASE_APP_PATHS.Campaign: {
            openKeys = [BASE_APP_PATHS.Campaign];
            break;
        }
    }
    return { openKeys, activeKeys };
}

/**
 * Combine a label with the count of notifications and a color for the badge
 * @param label The label we want to display
 * @param count The count of notifications
 * @param color The color of the badge
 * @returns
 */
export function getLabelWithNotificationCount(
    label: string,
    count: number,
    color: string,
    showLoader = false
): JSX.Element {
    return (
        <Flex justify="space-between" align="center">
            <span>{label}</span>
            {showLoader ? (
                <Spin indicator={<LoadingOutlined />} />
            ) : (
                <> {count > 0 && <Badge count={count} color={color} overflowCount={999} />}</>
            )}
        </Flex>
    );
}

/**
 * Get the inbox notification count
 * @param inboxType The type of inbox view shown for the user
 * @param count The count of notifications in the inbox object
 * @returns
 */
export function getInboxNotificationCount(
    inboxType: InboxDisplayType,
    count: number
): number | undefined {
    return inboxType === InboxDisplayType.Email && count > 0 ? count : undefined;
}

/**
 *
 * @param notifications
 * @returns
 */
export function getOutboxNotificationCountAndColor(
    notifications: SidebarNotificationMap,
    { pendingColor, errorColor }: Record<'pendingColor' | 'errorColor', string>
): { count: number; color: string } | undefined {
    const count = notifications.Pending + notifications.Error;
    const color = notifications.Error > 0 ? errorColor : pendingColor;
    return count > 0 ? { count, color } : undefined;
}
